import { Schema } from "../../Schema";
import { SchemaClient } from "../../common/GraphqlClient";
import { getAndProcessLinkedInstances } from "../common/external-sources/LinkedInstances";
import { getAndProcessSites } from "../common/external-sources/Sites";
import { getAndProcessTags } from "../common/external-sources/Tags";
import { getAndProcessLabAreas } from "../common/external-sources/LabAreas";
import { getAndProcessReasons } from "../common/external-sources/Reasons";
import { ClusterContext } from ".";
import {
  id,
  createdBy,
  updatedBy,
  mode,
  status,
  siteId,
  siteTimezone,
  location,
  createBelongingToGroup,
  equipmentNickName,
  description,
  cluster,
  createSiteName,
  createLinkedInstance,
  createBuildingLocation,
  createFloor,
  createRoom,
  createResponsiblePerson,
  responsibleProxy,
  createContactPerson,
  equipmentAdministrator,
  isBookable,
  isVisualized,
  qualificationDocuments,
  gxpRelevant,
  qualificationStatus,
  dateOfLastCalibration,
  dateOfNextMaintanance,
  systemStatus,
  createEditReason,
  editComment,
  entryType,
  currentUser,
  originalValues,
  createTags,
  createReviewers,
  createEquipmentCategory,
  createEquipmentModel,
  createVariant,
  createdAt,
  updatedAt,
  trackPositionInCluster,
  modelName,
  clusterId,
  positionInCluster,
  isClusterSWVersion,
  reviewAction,
  reviewId,
} from "../common/fields";
import {
  masterDataPicker,
  subEquipments,
  subEquipmentsPreview,
  leadingSoftwareVersionId,
  softwareVersion,
} from "./fields";

// Re-export types for backwards compatibility
export * from "../cluster";

/**
 * Builds the schema backing the cluster edit form (see EditClusterForm in the
 * instrument repository frontend). It carries only the fields the cluster form
 * consumes plus the machinery fields their getters depend on — no SOP/CSV or
 * standalone-equipment-only fields.
 */
export const prepareClusterSchema = async (client: SchemaClient): Promise<Schema> => {
  const [linkedInstances, sites, tags, labAreas, reasons] = await Promise.all([
    getAndProcessLinkedInstances(client),
    getAndProcessSites(client),
    getAndProcessTags(client),
    getAndProcessLabAreas(client),
    getAndProcessReasons(client),
  ]);

  const context: ClusterContext = { client, linkedInstances, sites, tags, labAreas, reasons };

  return {
    // Machinery / getter-referenced fields
    currentUser,
    entryType,
    modelName,
    clusterId,
    positionInCluster,
    isClusterSWVersion,
    reviewAction,
    reviewId,
    originalValues,
    id,
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
    mode,
    status,
    siteId,
    siteTimezone,
    location,
    linkedInstance: createLinkedInstance(context),
    siteName: createSiteName(context),
    // General information
    belongingToGroup: createBelongingToGroup(context),
    masterDataPicker,
    equipmentCategory: createEquipmentCategory(context),
    equipmentModel: createEquipmentModel(context),
    variant: createVariant(context),
    equipmentNickName,
    description,
    cluster,
    tags: createTags(context),
    // Location and occupation
    buildingLocation: createBuildingLocation(context),
    floor: createFloor(context),
    room: createRoom(context),
    responsiblePerson: createResponsiblePerson(context),
    responsibleProxy,
    contactPerson: createContactPerson(context),
    equipmentAdministrator,
    // Qualification and settings
    isBookable,
    isVisualized,
    qualificationDocuments,
    gxpRelevant,
    qualificationStatus,
    dateOfLastCalibration,
    dateOfNextMaintanance,
    softwareVersion,
    systemStatus,
    // Cluster components
    subEquipments,
    subEquipmentsPreview,
    leadingSoftwareVersionId,
    trackPositionInCluster,
    // Reason modal
    editReason: createEditReason(context),
    editComment,
    reviewers: createReviewers(context),
  };
};
